/**
 * The trainer's comment on a finished session — KANON view 05's foot.
 *
 * The canon draws it pinned to the bottom of the session panel: an eyebrow, a visibility line, a
 * field and two controls. That is what this is, and it is the WHOLE of what view 05 adds to the
 * panel — the owner's instruction is that the panel above it stays exactly as the code already has
 * it. The canon also draws a sentence under the controls saying a finished session's results are
 * not editable; the panel's own footer already says exactly that, two lines below, so it is not
 * repeated here.
 *
 * ── It is a feature, and today it has exactly ONE host ────────────────────────────────────────
 * `pages/assigned-plan-calendar`'s `SessionPanel`, and nothing else. This header used to claim two
 * hosts that "cannot import each other"; that was written for a plan the round did not take, and
 * the count was never true. It is recorded rather than quietly corrected because the reasoning it
 * produced still holds: the client detail DOES reach this composer, by carrying a workout execution
 * id to the calendar tab as `?exec=&comment=` and letting the one panel open there. Same component,
 * same overlay, one tab further — which is why a second host was never needed.
 *
 * `features/` is still where it belongs: a page may not import another page, and the day the detail
 * grows a panel of its own this is already on the right side of that line. One host is a fact about
 * today, not an argument for moving it.
 *
 * Its interface is one required prop — the session it comments on — plus the comment the host
 * already loaded, which the host has because it drew the note above.
 *
 * ── The clear is announced BEFORE it happens, not after ───────────────────────────────────────
 * Emptying the field and saving deletes the comment and brings the attention task back. The
 * sentence under the field changes to say so while the field is empty, so the outcome is readable
 * before the control is pressed rather than discovered from a task reappearing.
 */

import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import ink from '~/shared/ui/inkButton.module.css';

import { useSessionComment } from '../lib/useSessionComment';
import { SESSION_COMMENT_MAX_LENGTH } from '../model/sessionComment';
import styles from './SessionCommentComposer.module.css';

interface SessionCommentComposerProps {
  readonly workoutExecutionId: number;
  /** What the server holds. `''` where the session has never been commented on. */
  readonly savedComment: string;
  /**
   * Focus the field on mount. Set only where the trainer arrived here to write — the Przegląd
   * tab's `Skomentuj sesję`. A panel opened to READ a session must not steal focus into a textarea.
   */
  readonly autoFocus?: boolean;
  /**
   * The save landed, and the HOST is taking it from here.
   *
   * Supplied where saving the comment makes the composer's own container disappear — the pulpit's
   * queue cards, where the note resolves the attention item and the tile goes with it. A host that
   * passes this owns the focus landing as well: the field below is not focused, because focusing an
   * element that is about to unmount drops focus on `<body>`, which is the defect this whole effect
   * exists to prevent.
   *
   * It owns the REPORT too. The status line below stays empty for such a host, because the host
   * announces the save in its own live region and two regions announcing the same save read it out
   * twice — and this one is inside the element that is about to be invalidated away.
   */
  readonly onSaved?: () => void;
  /**
   * Quick replies the host offers under the field (the task card's, KANON "Karty zadań" 24 September
   * 2026). Pressing one puts its words INTO the draft — appended after what is already typed — and
   * focuses the field; nothing is sent until the trainer saves, so a quick reply is a start, never a
   * message sent on their behalf.
   */
  readonly drafts?: readonly string[];
  /**
   * The task card's shape (KANON "Karty zadań" `.k-compose`): one bordered box on the card's inset
   * ground, a field of three lines that gives its third back where the card is short, and the quick
   * replies on the controls' own line — the desk card has 730px for the whole task, and the panel's
   * roomy foot pushed the save button under its fold.
   */
  readonly compact?: boolean;
}

export function SessionCommentComposer({
  workoutExecutionId,
  savedComment,
  autoFocus = false,
  onSaved,
  drafts = [],
  compact = false,
}: SessionCommentComposerProps) {
  const { t } = useTranslation();
  const fieldId = `${useId()}comment`;
  const hintId = `${useId()}hint`;
  // Keyed on the session so switching cards inside an open panel starts a fresh draft rather than
  // carrying one session's half-written comment onto another. Remounting is the only correct
  // answer here — the draft is state the trainer owns, and reseeding it in an effect would
  // overwrite an edit in flight.
  const comment = useSessionComment(workoutExecutionId, savedComment);

  /**
   * Where focus goes when the save lands.
   *
   * A successful save makes the draft equal to what the server holds, which refuses both controls —
   * and a `disabled` element the browser is currently focusing drops focus on the FLOOR. Measured:
   * after pressing `Zapisz komentarz`, `document.activeElement` was `<body>`, so the trainer's next
   * Tab started again at the page's skip link, a whole screen away from the panel they were working
   * in. The refusal itself is right (`disabled`, not `aria-disabled`: nothing here needs explaining,
   * which is this app's rule for choosing between them), so the landing place is what has to be
   * said out loud.
   *
   * The field, because that is where the trainer was before they reached for the button and where
   * they would carry on if the comment needs another sentence. It is a focus MOVE on an explicit
   * action rather than a steal — the same reason `autoFocus` above is opt-in.
   */
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  /** Whether THIS composer reports the save. A host with `onSaved` does it, in its own region. */
  const saved = comment.justSaved && onSaved === undefined;
  // Through a ref, and the effect depends on the OUTCOME alone: a callback rebuilt by the host on
  // every render would re-run this effect on every render, and a host that announces from it would
  // then announce forever.
  const onSavedRef = useRef(onSaved);
  useEffect(() => {
    onSavedRef.current = onSaved;
  });
  useEffect(() => {
    if (!comment.justSaved) return;
    const handled = onSavedRef.current;
    if (handled === undefined) fieldRef.current?.focus();
    else handled();
  }, [comment.justSaved]);

  return (
    <section
      className={`${String(styles.composer)} ${compact ? String(styles.compact) : ''}`}
      aria-label={t('web:clientDetail.sessionComment.eyebrow')}
    >
      <div className={styles.head}>
        <label className={styles.eyebrow} htmlFor={fieldId}>
          {t('web:clientDetail.sessionComment.eyebrow')}
        </label>
        <em className={styles.visibility}>{t('web:clientDetail.sessionComment.visibility')}</em>
      </div>

      <textarea
        id={fieldId}
        ref={fieldRef}
        className={styles.field}
        value={comment.draft}
        onChange={(event) => {
          comment.setDraft(event.target.value);
        }}
        placeholder={t('web:clientDetail.sessionComment.placeholder')}
        maxLength={SESSION_COMMENT_MAX_LENGTH}
        aria-describedby={hintId}
        aria-invalid={comment.decision.tooLong || undefined}
        autoFocus={autoFocus}
        rows={3}
      />

      {compact ? null : quickReplies()}

      <div className={styles.foot}>
        {compact ? quickReplies() : null}
        {/* Only the clear warning. The other half of this used to read "Wyników wykonanej sesji się
          * nie edytuje" — which the panel's own footer says two lines below it, in the same words
          * ("Wykonanej sesji się nie edytuje"), because the composer is only ever offered on a
          * session that HAS an execution. Two sentences making one point is how a real warning stops
          * being read. Always mounted: it is this field's `aria-describedby`. */}
        <em className={styles.note} id={hintId}>
          {comment.decision.clears ? t('web:clientDetail.sessionComment.clearHint') : ''}
        </em>
        <button
          type="button"
          className={styles.cancel}
          onClick={comment.reset}
          disabled={comment.draft === savedComment && !comment.failed}
        >
          {t('web:clientDetail.sessionComment.cancel')}
        </button>
        <button
          type="button"
          className={`${String(ink.ink)} ${String(styles.save)}`}
          onClick={comment.save}
          disabled={!comment.decision.canSave || comment.saving}
        >
          {comment.saving
            ? t('web:clientDetail.sessionComment.saving')
            : t('web:clientDetail.sessionComment.save')}
        </button>
      </div>

      {/* Both outcomes are said out loud AND drawn. This was visually hidden, so the only signal a
        * sighted trainer got from a successful save was both controls going quiet — which looks
        * exactly like a save that never fired. Always mounted, because a `role="status"` inserted
        * together with its text is not reliably announced.
        *
        * Silent for a host that took the save over: it announces the save itself, from a region
        * that outlives this one, and two regions carrying the same sentence announce it twice. */}
      <p
        className={`${String(styles.status)} ${saved ? '' : String(styles.statusEmpty)}`}
        role="status"
      >
        {saved ? t('web:clientDetail.sessionComment.saved') : ''}
      </p>
      {comment.failed ? (
        <p className={styles.error} role="alert">
          {t('web:clientDetail.sessionComment.failed')}
        </p>
      ) : null}
    </section>
  );

  /** The host's quick replies: each puts its words INTO the draft and hands the field the focus. */
  function quickReplies() {
    if (drafts.length === 0) return null;
    return (
      <span className={styles.drafts}>
        {drafts.map((draft) => (
          <button
            key={draft}
            type="button"
            className={styles.draft}
            onClick={() => {
              const current = comment.draft.trimEnd();
              comment.setDraft(current === '' ? draft : `${current} ${draft}`);
              fieldRef.current?.focus();
            }}
          >
            {draft}
          </button>
        ))}
      </span>
    );
  }
}
